import { CustomJoyrideStep } from 'components/facets/CustomJoyrideTour'
import { editUserAttribute } from 'lib/api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ACTIONS, CallBackProps, STATUS } from 'react-joyride'
import { AppUser } from 'shared'
import { SetState } from 'types'
import { waitForSelector } from 'utils/lib'
import { MAX_LISTING_TABS } from './useListingTabsWithStocks'
import { FetchedStockInterface } from './useStockStore'

type Args = {
  user: AppUser
  setUser: SetState<AppUser>
  setRun: (v: boolean) => void
  addListingTab: () => void
  setStepIndex: (v: number | ((p: number) => number)) => void
  listingStock: FetchedStockInterface
}

const IDX = {
  TAB: 'tabManagement',
  ADD_TAB: 'createNewTab',
  EDIT_TAB: 'actionTab',
  DISPLAY_EDIT_TAB: 'moveBetweenTabs',
  INFO_CSV: 'uploadCsv',
  GO_TO_ADD_BY_CARD: 'uploadSingle',
  ADD_ITEM: 'addArticle',
  EDIT_PANEL: 'editArticle',
  END_TOUR: 'endTour',
} as const

export function useListingTour({
  user,
  setUser,
  setRun,
  addListingTab,
  setStepIndex,
  listingStock,
}: Args) {
  const { t } = useTranslation()

  const hasItem = (listingStock.stock?.length ?? 0) > 0
  const [waitingForItem, setWaitingForItem] = useState(!hasItem)

  // if for any reason there are already articles present, it prevents the panel from automatically opening when I land inside the listing
  const [shouldAutoOpenEditPanel, setShouldAutoOpenEditPanel] = useState(false)

  // I use useMemo so steps doesn't rebuild at every callBack
  const steps = useMemo<CustomJoyrideStep[]>(() => {
    const base: CustomJoyrideStep[] = [
      {
        id: IDX.TAB,
        target: '[data-testid="tab-1"]',
        content: t(`listingTour.${IDX.TAB}`),
      },
      {
        id: IDX.ADD_TAB,
        target: '[data-testid="add-new-tab"]',
        content: t(`listingTour.${IDX.ADD_TAB}`, { count: MAX_LISTING_TABS }),
      },
      {
        id: IDX.EDIT_TAB,
        target: '[data-testid="action-tab-2"]',
        content: t(`listingTour.${IDX.EDIT_TAB}`),
        meta: {
          clickSelector: '[data-testid="action-tab-2"]',
          waitForSelector: '[data-testid="edit-tab-options"]',
        },
      },
      {
        id: IDX.DISPLAY_EDIT_TAB,
        target: '[data-testid="edit-tab-options"]',
        placement: 'right',
        content: t(`listingTour.${IDX.DISPLAY_EDIT_TAB}`),
      },
      {
        id: IDX.INFO_CSV,
        target: '[data-testid="go-to-add-by-csv-btn"]',
        placement: 'right',
        content: t(`listingTour.${IDX.INFO_CSV}`),
      },
      {
        id: IDX.GO_TO_ADD_BY_CARD,
        target: '[data-testid="go-to-add-by-card-btn"]',
        placement: 'right',
        content: t(`listingTour.${IDX.GO_TO_ADD_BY_CARD}`),
        meta: {
          clickSelector: '[data-testid="go-to-add-by-card-btn"]',
          waitForSelector: '[data-testid="template-buy-price"]',
        },
      },
      {
        id: IDX.ADD_ITEM,
        target: '[data-testid="autocomplete-input"]',
        placement: 'right',
        disableBeacon: true,
        // the tour can only advance when the user actually adds a card
        // (handled by the effect that watches hasItem). Hiding the footer
        // prevents a Next click from desyncing Joyride's internal state
        // from the controlled stepIndex prop — a bail-out of setStepIndex
        // on the same value that only shows up in the production build.
        hideFooter: true,
        // without this, the dark overlay covers the autocomplete dropdown
        // that opens below the input, hiding the suggestions.
        disableOverlay: true,
        content: t(`listingTour.${IDX.ADD_ITEM}`),
      },
      {
        id: IDX.EDIT_PANEL,
        target: '[data-testid="article-being-added-box"]',
        content: t(`listingTour.${IDX.EDIT_PANEL}`),
      },
      {
        id: IDX.END_TOUR,
        target: 'body',
        placement: 'center',
        content: t(`listingTour.${IDX.END_TOUR}`),
      },
    ]

    return base
  }, [t])

  const idxById = useCallback((id: string) => steps.findIndex((s) => s.id === id), [steps])

  /**
   * This effect handles the automatic transition from the ADD_ITEM step to the EDIT_PANEL step.
   *
   * It is triggered when:
   * - we are in waiting mode (waitingForItem)
   * - automatic opening is allowed (shouldAutoOpenEditPanel)
   * - the user has actually added an item (hasItem === true)
   *
   * When all conditions are satisfied:
   * 1. it waits for the edit confirmation button to become available
   * 2. it automatically clicks it
   * 3. it waits for the edit panel to become visible
   * 4. it moves the tour to the EDIT_PANEL step
   * 5. it resets the waiting state to prevent re-execution
   *
   * This effect acts as a synchronization layer between:
   * - React state (hasItem)
   * - the asynchronous DOM that appears after adding the article
   * - the progression of the tour
   */

  useEffect(() => {
    if (!shouldAutoOpenEditPanel) return
    if (!waitingForItem) return
    if (!hasItem) return
    ;(async () => {
      const btnOk = await waitForSelector('[data-testid="edit-article-changes-btn"]', 4000)
      if (!btnOk) return
      ;(
        document.querySelector('[data-testid="edit-article-changes-btn"]') as HTMLElement | null
      )?.click()

      const panelOk = await waitForSelector('[data-testid="article-being-added-box"]', 4000)
      if (!panelOk) return

      const nextIndex = idxById(IDX.EDIT_PANEL)
      if (nextIndex >= 0) setStepIndex(nextIndex)

      setWaitingForItem(false)
      setShouldAutoOpenEditPanel(false)
    })()
  }, [shouldAutoOpenEditPanel, waitingForItem, hasItem, steps, idxById])

  const runTour = useCallback(
    async (data: CallBackProps) => {
      const { status, type, index, action } = data

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        setRun(false)
        const notifications = user.enabledNotifications ?? {}
        const response = await editUserAttribute(
          {
            ...notifications,
            displayListingTour: false,
          },
          'enabledNotifications'
        )
        setUser({ ...user, ...response })
        return
      }

      // I use this to avoid making the exact match with the IDX id, otherwise if I add steps in idx or move them I have to be careful about the order
      const stepId = steps[index]?.id

      if (type === 'error:target_not_found') {
        // keep the tour on the same step, but only after the target exists again
        const targetSelector = steps[index]?.target as string

        if (typeof targetSelector === 'string' && targetSelector !== 'body') {
          const ok = await waitForSelector(targetSelector, 4000)
          if (ok) setStepIndex(index)
        } else {
          setStepIndex(index)
        }
        return
      }

      // step:before -> all actions that require UI preparation
      if (type === 'step:before') {
        if (stepId === IDX.INFO_CSV) {
          window.dispatchEvent(new Event('pt:close-tab-menu'))
          await new Promise((r) => setTimeout(r, 50))
        }

        if (stepId === IDX.ADD_ITEM) {
          const ok = await waitForSelector('[data-testid="autocomplete-input"]', 4000)
          if (!ok) {
            // resta su questo step finché non è pronto
            setStepIndex(idxById(IDX.ADD_ITEM))
            return
          }

          // as soon as you enter the step, you go into waiting mode
          if (!hasItem) {
            setWaitingForItem(true)
            setShouldAutoOpenEditPanel(true)
          } else {
            // If for some reason you already have items, skip to the next one immediately
            const nextIndex = idxById(IDX.EDIT_PANEL)
            if (nextIndex >= 0) setStepIndex(nextIndex)
          }
        }

        return
      }

      if (type !== 'step:after') return

      // step:after -> click to be made after viewing the message
      // this is a step after: first I show the tooltip on the button that adds the tabs, then I actually add the tab
      if (stepId === IDX.ADD_TAB) {
        addListingTab()
        setStepIndex(index + 1)
        return
      }

      if (stepId === IDX.EDIT_PANEL) {
        // close the edit modal and wait for it to actually unmount before
        // advancing, so END_TOUR is rendered on a clean page instead of on
        // top of the Dialog backdrop.
        window.dispatchEvent(new Event('pt:close-edit-article'))
        const start = Date.now()
        while (
          document.querySelector('[data-testid="article-being-added-box"]') &&
          Date.now() - start < 5000
        ) {
          await new Promise((r) => setTimeout(r, 30))
        }
        const delta = action === ACTIONS.PREV ? -1 : 1
        setStepIndex(index + delta)
        return
      }

      // execution of the specifications defined within meta
      {
        const step = steps[index]
        const { clickSelector, waitForSelector: waitSel } = step?.meta ?? {}

        if (clickSelector) {
          void (document.querySelector(clickSelector) as HTMLElement | null)?.click()
        }

        if (waitSel) {
          const ok = await waitForSelector(waitSel)
          if (!ok) return
        }
      }

      if (stepId === IDX.ADD_ITEM) {
        // Do nothing: the wait has already been armed in step:before
        if (!hasItem) {
          // stay here otherwise the stepper will move forward and end up in a strange state
          setStepIndex(idxById(IDX.ADD_ITEM))
        }
        return
      }

      // delta is used to understand whether the next or back button has been pressed
      const delta = action === ACTIONS.PREV ? -1 : 1
      setStepIndex(index + delta)
    },
    [steps, hasItem, addListingTab, setRun, setStepIndex, idxById, user]
  )

  return { steps, runTour }
}
